// A person's account in each product Phoneware sells, as mock M3 draws them (docs/plans/lens-dealer-portal/accounts.html,
// spec 3.6, #1776): Settings -> Your accounts and the person sheet use these rows. The server sends each product's row
// state, accounts and evidence; the words here are M3's, written for the reader ("you") or about someone ("Dana").
// Rows Omni can't search (links by email, no source) show how to sign in there instead (decision D4), Billing among them
// while OneBill has no list of Self-Care users.
import React, { useEffect, useState } from "react";
import type { AccountEvidence, AccountProduct, AccountRow, AccountRowAccount, LensUser, PersonAccounts } from "@phoneware/lens-domain";
import { Icon, type IconName } from "../icons.js";
import { firstName, whenIn } from "../format.js";
import { useSession, viewerKind } from "../session.js";
import { Chip } from "../ui.js";

/** The launcher's products as a person reads them (accounts.html:568-576). */
export const PRODUCT: Record<AccountProduct, { name: string; what: string; icon: IconName }> = {
  edge: { name: "Edge", what: "Phone system", icon: "phone" },
  texting: { name: "Texting", what: "Business texting, by Clerk", icon: "message-square-text" },
  billing: { name: "Billing", what: "OneBill Self-Care", icon: "receipt" },
  m365: { name: "Microsoft 365", what: "Work or school account", icon: "layout-grid" },
  incontrol: { name: "InControl", what: "Peplink routers and failover", icon: "router" },
  meta: { name: "Meta", what: "Metaswitch phone system, CommPortal", icon: "server" },
};

/** The person a list of rows is about. */
export type AccountsPerson = Pick<LensUser, "name" | "email" | "role">;

/** A row Omni can't search: the product finds the person by email itself, or Omni has nothing to match by. */
export function signsInThere(row: AccountRow): boolean {
  return row.state === "vendor_links_by_email" || row.state === "no_source";
}

/**
 * Found, suggested and none found, over the rows Omni searches; and how many it can't. A product Omni couldn't reach
 * in the latest check keeps the state it had, and is never counted as none found.
 */
export function accountCounts(accounts: PersonAccounts): { searched: number; found: number; suggested: number; none: number; there: number } {
  const searched = accounts.rows.filter((r) => !signsInThere(r));
  return {
    searched: searched.length,
    found: searched.filter((r) => r.state === "linked" || r.state === "matched").length,
    suggested: searched.filter((r) => r.state === "suggested").length,
    none: searched.filter((r) => r.state === "none" && !accounts.unavailable.includes(r.product)).length,
    there: accounts.rows.length - searched.length,
  };
}

const NUMBER_WORDS = ["no", "one", "two", "three", "four", "five", "six", "seven"];
/** "three", for the lede's counts. */
export function numberWord(n: number): string {
  return NUMBER_WORDS[n] ?? String(n);
}

/** "Rejected 215@TVCOffice, 2026-10-19T16:02:00.000Z": the account and when, from a rejection's row evidence. */
function rejectionOf(e: AccountEvidence): { key: string; at: string | null } | null {
  if (e.kind !== "reviewed") return null;
  const m = /^Rejected (.+?)(?:, (\d{4}-\d\d-\d\dT\S+))?$/.exec(e.detail);
  return m ? { key: m[1], at: m[2] ?? null } : null;
}

/** The pronouns a row is written in: about you, or about Dana. */
interface Words {
  You: string;
  you: string;
  your: string;
  /** "sign" or "signs". */
  s: string;
  first: string;
}
function wordsFor(person: AccountsPerson, self: boolean): Words {
  const first = firstName(person.name);
  return self ? { You: "You", you: "you", your: "your", s: "", first } : { You: first, you: first, your: `${first}\u2019s`, s: "s", first };
}

/** What a row can do when it is the reader's own. Undo names the account by its key, as the rejection's evidence does. */
export interface RowActions {
  busy: boolean;
  notMe: (account: AccountRowAccount) => void;
  undo: (key: string) => void;
  /** Opens Add Microsoft sign-in (mock M3 state 06). Null while someone acts as the person: only they add it. */
  addMicrosoft: (() => void) | null;
}

type Item = [string, React.ReactNode];

function Ev({ items }: { items: Item[] }): React.ReactElement {
  return (
    <ul className="ev">
      {items.map(([k, d], i) => (
        <li key={i}>
          <span className="k">{k}</span>
          <span className="d">{d}</span>
        </li>
      ))}
    </ul>
  );
}

function KeyLine({ value, lite }: { value: string; lite: string }): React.ReactElement {
  return (
    <div className="acc-key">
      <span className="mono-key">{value}</span> <span className="lite">{lite}</span>
    </div>
  );
}

function None({ children }: { children: React.ReactNode }): React.ReactElement {
  return <div className="acc-key none">{children}</div>;
}

function Where({ host }: { host: string }): React.ReactElement {
  return (
    <div className="acc-key">
      <span className="where">
        <Icon name="globe" size={13} />
        {host}
      </span>
    </div>
  );
}

/** Evidence the words below don't cover, as the server wrote it. */
function rawItems(evidence: readonly AccountEvidence[]): Item[] {
  return evidence.map((e) => [e.kind.replace(/_/g, " "), e.detail]);
}

/** `reached` is false when Omni couldn't reach Edge in the latest check: what it had stays, and nothing says none found. */
function edgeBody(row: AccountRow, person: AccountsPerson, w: Words, self: boolean, actions: RowActions | null, reached: boolean): React.ReactNode {
  const parts: React.ReactNode[] = [];
  for (const a of row.accounts) {
    if (a.state === "linked") {
      // The LoginLink's time rides in its evidence: "Connect to Omni in Edge, 2026-10-14T16:02:00.000Z".
      const at = a.evidence.map((e) => (e.kind === "login_link" ? /\d{4}-\d\d-\d\dT[^\s,]+/.exec(e.detail)?.[0] : undefined)).find((x) => x) ?? null;
      parts.push(
        <div className="acc-acct" key={a.matchId}>
          <KeyLine value={a.key} lite={`Edge user ${person.name}`} />
          <Ev
            items={[
              [
                "Linked",
                `This Edge user was linked to ${self ? "you" : w.first}${at ? ` on ${new Date(at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : ""}.`,
              ],
            ]}
          />
        </div>,
      );
      continue;
    }
    const noEmail = a.evidence.some((e) => e.kind === "provider_subject" && /has no email/.test(e.detail));
    const items: Item[] = [];
    for (const e of a.evidence) {
      if (e.kind === "verified_email") {
        items.push([
          "Email",
          <>
            This Edge user&rsquo;s email is <b>{person.email}</b>
            {`, the email ${w.you} sign${w.s} in to Omni with.`}
          </>,
        ]);
      } else if (e.kind === "provider_subject" && /has no email/.test(e.detail)) {
        items.push(["Email", "This Edge user has no email."]);
      } else if (e.kind === "provider_subject") {
        // ns-users matches the single sign-on list by the email the person signs in to Omni with.
        items.push([
          "SSO",
          <>
            This Edge user&rsquo;s single sign-on list has <b>{person.email}</b>
            {`, the email ${w.you} sign${w.s} in to Omni with.`}
          </>,
        ]);
      } else {
        items.push(...rawItems([e]));
      }
    }
    parts.push(
      <div className="acc-acct" key={a.matchId}>
        <KeyLine value={a.key} lite={noEmail ? "Edge user, no email" : a.label && a.label !== a.key ? `Edge user ${a.label}` : "Edge user"} />
        <Ev items={items} />
        {/* #1660 restores the link step (Connect to Omni in Edge, then Omni's /sso/link page) once that flow works. */}
        {self && actions ? (
          <>
            <p className="said">Linking from Edge isn&rsquo;t available yet, so this stays a suggestion.</p>
            <div className="btnrow">
              <button className="btn small white" type="button" data-testid={`not-me-${a.key}`} disabled={actions.busy} onClick={() => actions.notMe(a)}>
                <Icon name="user-x" size={13} />
                Not me
              </button>
            </div>
          </>
        ) : (
          <p className="said">{w.first} can link it once linking from Edge is available.</p>
        )}
      </div>,
    );
  }
  const rejected = row.evidence.map(rejectionOf).filter((r): r is { key: string; at: string | null } => r !== null);
  if (row.accounts.length === 0 && reached) parts.push(<None key="none">No Edge user found</None>);
  for (const r of rejected) {
    parts.push(
      <React.Fragment key={`rejected-${r.key}`}>
        <Ev
          items={[
            [
              "Not me",
              <>
                {w.You} said <b>{r.key}</b> isn&rsquo;t {self ? "you" : w.first}
                {r.at ? ` on ${new Date(r.at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}. Omni won&rsquo;t suggest it again.
              </>,
            ],
          ]}
        />
        {self && actions ? (
          <p className="said">
            <button className="link" type="button" data-testid={`undo-not-me-${r.key}`} disabled={actions.busy} onClick={() => actions.undo(r.key)}>
              Undo
            </button>
          </p>
        ) : null}
      </React.Fragment>,
    );
  }
  if (reached && row.accounts.length === 0 && rejected.length === 0) {
    parts.push(
      <Ev
        key="searched"
        items={[
          [
            "Searched",
            <>
              No Edge user has <b>{person.email}</b>
              {" as its email or in its single sign-on list."}
            </>,
          ],
        ]}
      />,
    );
    if (self)
      parts.push(
        <p className="said" key="said">
          Use Edge? Ask whoever runs your Edge domain to put {person.email} on your Edge user, then check again.
        </p>,
      );
  }
  return parts;
}

function textingBody(row: AccountRow, person: AccountsPerson, w: Words): React.ReactNode {
  if (row.accounts.length === 0) {
    return (
      <>
        <None>No Texting user found</None>
        <Ev
          items={[
            [
              "Searched",
              <>
                No Texting user on any team has <b>{person.email}</b>.
              </>,
            ],
          ]}
        />
      </>
    );
  }
  return row.accounts.map((a) => (
    <div className="acc-acct" key={a.matchId}>
      <KeyLine value={person.email} lite={`on the ${a.label} team`} />
      <Ev items={[["Email", `A Texting user on the ${a.label} team has ${w.your} verified email.`]]} />
    </div>
  ));
}

function m365Body(row: AccountRow, w: Words, actions: RowActions | null): React.ReactNode {
  if (row.accounts.length === 0) {
    return (
      <>
        <None>Microsoft sign-in not added</None>
        <Ev items={[["Sign-in", `Omni knows ${w.your} Microsoft 365 account once ${w.you} sign${w.s} in to Omni with Microsoft. Work or school accounts only.`]]} />
        {actions?.addMicrosoft ? (
          <div className="btnrow">
            <button className="btn small" type="button" data-testid="add-microsoft" disabled={actions.busy} onClick={actions.addMicrosoft}>
              <Icon name="plus" size={13} />
              Add Microsoft sign-in
            </button>
          </div>
        ) : null}
      </>
    );
  }
  return row.accounts.map((a) => (
    <div className="acc-acct" key={a.matchId}>
      <KeyLine value={a.key} lite={`work account, ${a.key.split("@")[1] ?? ""}`} />
      <Ev items={[["Sign-in", `This Microsoft work account is on ${w.your} Omni sign-in.`]]} />
    </div>
  ));
}

/** Billing once OneBill can look Self-Care users up (spec 3.6): matched by username or email, or none found. */
function billingBody(row: AccountRow, person: AccountsPerson): React.ReactNode {
  if (row.accounts.length === 0) {
    return (
      <>
        <None>No Self-Care user found</None>
        <Ev
          items={[
            [
              "Searched",
              <>
                No OneBill Self-Care user has <b>{person.email}</b> as a username or email.
              </>,
            ],
          ]}
        />
      </>
    );
  }
  return row.accounts.map((a) => (
    <div className="acc-acct" key={a.matchId}>
      <KeyLine value={a.key} lite="OneBill Self-Care user" />
      <Ev items={[["Email", "A OneBill Self-Care user has this username."]]} />
    </div>
  ));
}

/** How each product Omni can't search finds the person (accounts.html:799-811, and Billing under D4). */
function signInThereBody(row: AccountRow, person: AccountsPerson, w: Words, self: boolean): React.ReactNode {
  const email = <b>{person.email}</b>;
  switch (row.product) {
    case "incontrol": {
      const kind = viewerKind(person);
      let how: React.ReactNode;
      switch (kind) {
        case "admin":
          how = <>Choose Continue with Google and use {w.your} phoneware.us account, {email}.</>;
          break;
        case "dealer":
          how = <>Choose Continue with Google or Continue with Microsoft, and use {email}.</>;
          break;
        default: {
          const unhandled: never = kind;
          throw new Error(`Unknown person ${String(unhandled)}`);
        }
      }
      return (
        <>
          <Where host="incontrol2.peplink.com" />
          <Ev
            items={[
              ["How", how],
              ["Why", "Peplink matches InControl accounts by email itself. Its API has no list of users, so Omni can\u2019t show this one."],
            ]}
          />
        </>
      );
    }
    case "meta":
      return (
        <>
          <Where host="uccommportal.com" />
          <Ev
            items={[
              ["How", `Sign in to CommPortal with ${w.your} phone number and CommPortal password.`],
              ["Why", "Meta keeps a phone number and a name for each user but no email, so Omni has nothing to match by."],
            ]}
          />
        </>
      );
    case "billing":
      return (
        <>
          <Where host="phoneware.mycloudbill.com/selfcare" />
          <Ev
            items={[
              [
                "How",
                self ? (
                  <>
                    Sign in with your Self-Care username and password. If you don&rsquo;t have one, email <b>billing@phoneware.us</b>.
                  </>
                ) : (
                  `Sign in with ${w.your} Self-Care username and password.`
                ),
              ],
              ["Why", "OneBill\u2019s API has no list of Self-Care users, so Omni can\u2019t show this one."],
            ]}
          />
        </>
      );
    case "edge":
    case "texting":
    case "m365":
      return <Ev items={rawItems(row.evidence)} />;
    default: {
      const unhandled: never = row.product;
      throw new Error(`Unknown product ${String(unhandled)}`);
    }
  }
}

function stateChip(row: AccountRow, unreached: boolean): React.ReactElement {
  if (unreached) return <Chip kind="quiet" icon="circle-alert">Couldn&rsquo;t reach</Chip>;
  switch (row.state) {
    case "linked":
      return <Chip kind="ok" icon="link">Linked</Chip>;
    case "matched":
      return <Chip kind="ok" icon="badge-check">Matched by email</Chip>;
    case "suggested":
      // Nothing waits on the person while linking from Edge isn't available (#1660).
      return <Chip kind="info" icon="search">Suggested</Chip>;
    case "none":
      // Microsoft 365 is found through Microsoft sign-in: until it is added there is nothing to look for.
      return row.product === "m365" ? <Chip kind="quiet" icon="circle-minus">Not added</Chip> : <Chip kind="quiet" icon="circle-minus">None found</Chip>;
    case "vendor_links_by_email":
      return <Chip kind="info" icon="mail">Links by email</Chip>;
    case "no_source":
      return <Chip kind="quiet" icon="eye-off">Omni can&rsquo;t see it</Chip>;
    default: {
      const unhandled: never = row.state;
      throw new Error(`Unknown row state ${String(unhandled)}`);
    }
  }
}

/**
 * One product's row. `unavailable` means Omni couldn't reach the product's source in the latest check: the row keeps
 * what it had, says so, and never claims none found.
 */
export function AccountRowView({ row, person, self, actions, unavailable }: { row: AccountRow; person: AccountsPerson; self: boolean; actions: RowActions | null; unavailable: boolean }): React.ReactElement {
  const w = wordsFor(person, self);
  const p = PRODUCT[row.product];
  // Nothing stored and nothing reached: the row has no account to show and no search to report.
  const unreached = unavailable && row.accounts.length === 0;
  let body: React.ReactNode;
  if (signsInThere(row)) body = signInThereBody(row, person, w, self);
  else if (row.product === "edge") body = edgeBody(row, person, w, self, actions, !unavailable);
  else if (unreached) body = null;
  else if (row.product === "texting") body = textingBody(row, person, w);
  else if (row.product === "m365") body = m365Body(row, w, self ? actions : null);
  else if (row.product === "billing") body = billingBody(row, person);
  else body = <Ev items={rawItems(row.evidence)} />;
  const tile = unreached || row.state === "none" || row.state === "no_source" ? "ptile quiet" : "ptile";
  return (
    <div
      className="acc"
      data-prod={row.product}
      data-state={row.state}
      data-unavailable={unavailable ? "true" : undefined}
      data-testid={`acc-${row.product}`}
    >
      <div className="acc-p">
        <span className={tile}>
          <Icon name={p.icon} />
        </span>
        <span>
          <b>{p.name}</b>
          <span className="what">{p.what}</span>
        </span>
      </div>
      <div className="acc-s">{stateChip(row, unreached)}</div>
      <div className="acc-m">
        {body}
        {unavailable ? (
          <p className="said" data-testid={`unavailable-${row.product}`}>
            {self ? `Omni couldn\u2019t reach ${p.name} just now, so this may be out of date. Check again in a minute.` : `Omni couldn\u2019t reach ${p.name} just now, so this may be out of date.`}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** The summary chips over the rows: found, suggested, none found, and sign in there. */
export function SummaryChips({ accounts }: { accounts: PersonAccounts }): React.ReactElement {
  const n = accountCounts(accounts);
  return (
    <div className="btnrow acc-sum" data-testid="accounts-summary">
      <Chip kind={n.found ? "ok" : "quiet"} icon="circle-check">
        {n.found} of {n.searched} found
      </Chip>
      {n.suggested ? (
        <Chip kind="info" icon="search">
          {n.suggested} suggested
        </Chip>
      ) : null}
      {n.none ? (
        <Chip kind="quiet" icon="circle-minus">
          {n.none} none found
        </Chip>
      ) : null}
      {n.there ? (
        <Chip kind="info" icon="mail">
          {n.there} sign in there
        </Chip>
      ) : null}
    </div>
  );
}

/** "Omni finds these", then "Sign in to these yourself" (or "Signed in to directly" about someone else). */
export function AccountGroups({ accounts, person, self, actions }: { accounts: PersonAccounts; person: AccountsPerson; self: boolean; actions: RowActions | null }): React.ReactElement {
  const w = wordsFor(person, self);
  const searched = accounts.rows.filter((r) => !signsInThere(r));
  const there = accounts.rows.filter(signsInThere);
  const thereTitle = self ? "Sign in to these yourself" : "Signed in to directly";
  return (
    <>
      {searched.length ? (
        <section className="acc-g" aria-label="Omni finds these" data-testid="accounts-found">
          <div className="acc-g-h">
            <h3>Omni finds these</h3>
            <span className="note">
              {`From the email ${w.you} sign${w.s} in to Omni with. Checked ${whenIn(accounts.checkedAt)}.`}
            </span>
          </div>
          {searched.map((row) => (
            <AccountRowView key={row.product} row={row} person={person} self={self} actions={actions} unavailable={accounts.unavailable.includes(row.product)} />
          ))}
        </section>
      ) : null}
      {there.length ? (
        <section className="acc-g" aria-label={thereTitle} data-testid="accounts-there">
          <div className="acc-g-h">
            <h3>{thereTitle}</h3>
            <span className="note">Omni can&rsquo;t read who has an account in these. Here&rsquo;s how each one finds {self ? "you" : w.first}.</span>
          </div>
          {there.map((row) => (
            <AccountRowView key={row.product} row={row} person={person} self={self} actions={null} unavailable={accounts.unavailable.includes(row.product)} />
          ))}
        </section>
      ) : null}
    </>
  );
}

/** Each person's accounts for a people table, one request per person. List only people the reader may read. */
export function useAccountsOf(emails: readonly string[]): Record<string, PersonAccounts> {
  const { api } = useSession();
  const [found, setFound] = useState<Record<string, PersonAccounts>>({});
  const key = emails.join("\n");
  useEffect(() => {
    let live = true;
    for (const email of emails) {
      api.personAccounts(email).then(
        (accounts) => {
          if (live) setFound((f) => ({ ...f, [email]: accounts }));
        },
        // A person whose accounts fail to load keeps an empty Accounts cell; the sheet says why when it is opened.
        () => undefined,
      );
    }
    return () => {
      live = false;
    };
    // The list of people decides when to load; `emails` is a fresh array every render.
  }, [key]);
  return found;
}

/** The Accounts cell of a people table: "2 of 3 found", and "1 suggested" when Omni has a suggestion. */
export function AccountsCell({ accounts }: { accounts: PersonAccounts | undefined }): React.ReactElement | null {
  if (!accounts) return null;
  const n = accountCounts(accounts);
  return (
    <span className="acc-cell">
      <Chip kind={n.found ? "ok" : "quiet"}>
        {n.found} of {n.searched} found
      </Chip>
      {n.suggested ? <Chip kind="info">{n.suggested} suggested</Chip> : null}
    </span>
  );
}
